import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { NoteEyebrow } from "../NoteEyebrow";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric" }) : "");
const GRASS = "#0A9B06";

/* Passport-stamp status: one colour per campaign state, one handwritten aside. */
export const STAMP = {
  draft: { label: "Draft", cls: "border-ink text-ink", when: (d) => d.created_at && `started ${fmt(d.created_at)}`, note: "build it once, submit it once" },
  pending_review: { label: "In review", cls: "border-cobalt text-cobalt", when: (d) => d.submitted_at && `since ${fmt(d.submitted_at)}`, note: "your aunties and uncles have it" },
  live: { label: "Live", cls: "border-[#0A9B06] text-[#0A9B06]", when: (d) => d.starts_at && `since ${fmt(d.starts_at)}`, note: "go tell the group chat" },
  paused: { label: "Paused", cls: "border-yellow bg-yellow text-ink", when: () => "by our team, for now", note: "paused, not gone" },
  rejected: { label: "Needs edits", cls: "border-scarlet text-scarlet", when: (d) => d.reviewed_at && `returned ${fmt(d.reviewed_at)}`, note: "a quick fix and it's back in" },
  closed: { label: "Closed", cls: "border-olive text-olive", when: (d) => d.closed_at && `ended ${fmt(d.closed_at)}`, note: "what a run" },
};

const ENTRANCE = { duration: 0.5, delay: 0.4, ease: [0.22, 1, 0.36, 1] };
const SETTLE = { duration: 0.9, ease: "easeOut", times: [0, 0.5, 0.8, 1] };

/** The approval moment: when the status turns live while the student is looking, the stamp re-lands in grass. */
const useApprovalMoment = (status) => {
  const prev = useRef(status);
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (prev.current !== status) {
      if (status === "live") setCelebrate(true);
      prev.current = status;
    }
  }, [status]);
  return celebrate;
};

export const StatusStamp = ({ draft }) => {
  const s = STAMP[draft.status] || STAMP.draft;
  const when = s.when(draft);
  const celebrate = useApprovalMoment(draft.status);
  return (
    <div className="flex flex-col items-start gap-3 lg:items-end" data-testid="dashboard-status-stamp" data-status={draft.status} data-celebrate={celebrate}>
      <div className="relative">
        {celebrate && (
          <motion.span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-md"
            style={{ backgroundColor: GRASS }}
            initial={{ opacity: 0.35, scale: 1 }}
            animate={{ opacity: 0, scale: 1.9 }}
            transition={{ duration: 1.1, ease: "easeOut" }}
            data-testid="dashboard-status-glow"
          />
        )}
        <motion.div
          initial={{ opacity: 0, rotate: -10, scale: 1.1 }}
          animate={celebrate ? { opacity: 1, scale: [1.35, 0.95, 1.04, 1], rotate: [-14, -1, -5, -3] } : { opacity: 1, rotate: -3, scale: 1 }}
          transition={celebrate ? SETTLE : ENTRANCE}
          className={`relative inline-flex flex-col rounded-md border-2 px-5 py-3 transition-[color,border-color,background-color] duration-700 ${s.cls}`}
        >
          <span className="font-serif text-3xl font-semibold leading-none" data-testid="dashboard-status-label">{s.label}</span>
          {when && <span className="mt-1.5 text-[13px] font-medium">{when}</span>}
        </motion.div>
      </div>
      <motion.div key={draft.status} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: celebrate ? 0.7 : 0.6 }}>
        <NoteEyebrow arrow="↑" className="whitespace-nowrap lg:origin-right" data-testid="dashboard-status-note">{s.note}</NoteEyebrow>
      </motion.div>
    </div>
  );
};
